import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import Image from 'next/image';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { GlobalPortal } from '../portal/GlobalPortal';

const OriginalImageAreaWrap = styled(Image)`
  position: relative;
  width: 100%;
  height: 100%;
  opacity: 0;
`;

const ImageWrap = styled.div<{
  $isZoom: boolean;
  $initialImageInfo: { height: number; width: number; x: number; y: number };
}>`
  ${({ $isZoom }) =>
    $isZoom
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0;
        `}
  position: absolute;
  width: ${({ $initialImageInfo }) => $initialImageInfo.width}px;
  height: ${({ $initialImageInfo }) => $initialImageInfo.height}px;
  left: ${({ $initialImageInfo }) => $initialImageInfo.x}px;
  top: ${({ $initialImageInfo }) => $initialImageInfo.y}px;
  z-index: 999;
  transition: opacity 0.15s ease-in-out;
  overflow: visible !important;

  .zoom-wrapper,
  .zoom-content {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible !important;

    img {
      object-fit: contain;
    }
  }
`;

const BgWrap = styled.div<{ $isZoom: boolean }>`
  opacity: ${({ $isZoom }) => ($isZoom ? 1 : 0)};
  position: fixed;
  inset: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: none;
  z-index: 999;
  transition: opacity 0.2s ease-in-out;
`;

interface Props {
  thumbnailPhotoUrl: string;
}

const PhotoCardImageZoom = ({ thumbnailPhotoUrl }: Props) => {
  const [isTouchActive, setIsTouchActive] = useState(false);
  const [isZoom, setZoom] = useState(false);

  // 줌 되지 않았을 때의 최초 이미지 영역 계산용
  const originalImageAreaRef = useRef<HTMLImageElement | null>(null);
  const [imageInfo, setImageInfo] = useState({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  });

  useEffect(() => {
    if (originalImageAreaRef && originalImageAreaRef.current) {
      const { width, height, x, y } =
        originalImageAreaRef.current.getBoundingClientRect();
      setImageInfo({
        width,
        height,
        x,
        y,
      });
    }
  }, [
    originalImageAreaRef?.current,
    originalImageAreaRef?.current?.x,
    originalImageAreaRef?.current?.y,
  ]);

  const handleTouchStart = (event: React.TouchEvent) => {
    if (event.touches.length > 0) {
      setIsTouchActive(true);
    }
  };

  return (
    <>
      <OriginalImageAreaWrap
        ref={originalImageAreaRef}
        src={thumbnailPhotoUrl}
        alt={thumbnailPhotoUrl}
        fill
        sizes="1px"
        loading="lazy"
        onTouchStart={handleTouchStart}
      />
      <GlobalPortal.Consumer>
        <BgWrap $isZoom={isZoom}>
          <ImageWrap
            $isZoom={isZoom}
            $initialImageInfo={imageInfo}
            style={{
              /* 두 손가락 터치 시 pointer-events: auto, 아니면 none */
              pointerEvents: isTouchActive ? 'auto' : 'none',
            }}
          >
            <TransformWrapper
              minScale={1}
              maxScale={4}
              disablePadding
              wheel={{ wheelDisabled: true, step: 0.08 }}
              pinch={{ step: 10 }}
              doubleClick={{ disabled: true }}
              onZoomStart={() => {
                setZoom(true);
              }}
              onZoomStop={(ref) => {
                setZoom(false);
                setIsTouchActive(false);
                ref.resetTransform();
              }}
            >
              <TransformComponent
                wrapperClass="zoom-wrapper"
                contentClass="zoom-content"
              >
                <Image
                  src={thumbnailPhotoUrl}
                  alt={thumbnailPhotoUrl}
                  fill
                  sizes="600px"
                  loading="lazy"
                />
              </TransformComponent>
            </TransformWrapper>
          </ImageWrap>
        </BgWrap>
      </GlobalPortal.Consumer>
    </>
  );
};

export default PhotoCardImageZoom;
